/**
 * Transforms a raw string value into a display-friendly format.
 *
 * - Returns `null` for `null` or `undefined` input.
 * - Splits the value on whitespace and title-cases each word.
 * - Special cases:
 *   - `"uv"` (any casing) → `"UV"`
 *   - `"or"` (any casing) → `"or"` (kept lowercase)
 *
 * @example
 * transformDisplayValue("gloss film lam") // → "Gloss Film Lam"
 * transformDisplayValue("uv coating")     // → "UV Coating"
 * transformDisplayValue(null)             // → null
 */
export function transformDisplayValue(value: string | null | undefined): string | null {
  if (value == null) return null;
  const words = value.trim().split(/\s+/);
  return words
    .map((word) => {
      if (word.toLowerCase() === "uv") return "UV";
      if (word.toLowerCase() === "or") return "or";
      if (!word) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

/**
 * Formats an ancillary code name by title-casing each " - "-delimited segment.
 *
 * @example
 * formatAncillaryCodeName("COATING - GLOSS FILM LAM - Run") // → "Coating - Gloss Film Lam - Run"
 */
export function formatAncillaryCodeName(value: string | null | undefined): string | null {
  if (value == null) return null;
  return value
    .split(" - ")
    .map((part) => transformDisplayValue(part) ?? part)
    .join(" - ");
}

/**
 * Compares two strings alphabetically, ignoring case.
 * Intended for use as a comparator with `Array.prototype.sort`.
 *
 * @returns `-1` if `a` comes before `b`, `1` if after, `0` if equal.
 *
 * @example
 * ["Banana", "apple", "Cherry"].sort(compareStringsAlphabeticallyIgnoreCase)
 * // → ["apple", "Banana", "Cherry"]
 */
export function compareStringsAlphabeticallyIgnoreCase(
  a: string,
  b: string
): number {
  const aToLower = a.toLowerCase();
  const bToLower = b.toLowerCase();

  return aToLower < bToLower ? -1 : aToLower > bToLower ? 1 : 0;
}
